import React, { FC, Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import MoreHorzIcon from '@material-ui/icons/MoreHoriz';

import {
  FeedKey,
  Group,
  useGetGroupFollowersQuery,
  UserFeedFollow,
  UserFeedFollowGroupFragment,
} from 'api';
import { Alert, FollowToggle } from 'components';
import { isAdminUser } from 'helpers';
import { useCurrentUser, useNotify } from 'hooks';
import { generateLink } from 'Links';

import { isGroupOwnerOrModerator } from '../../helpers';

interface Props {
  selectedGroup: UserFeedFollowGroupFragment;
  enableSort?: boolean;
  sort?: boolean;
  onSort?: () => void;
  allowSort: boolean;
}

const FeedMenu: FC<Props> = props => {
  const { selectedGroup, enableSort, sort, onSort, allowSort } = props;

  const notify = useNotify();
  const currentUser = useCurrentUser();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const [open, setOpen] = useState<boolean>(false);

  function toggleConfirm() {
    setOpen(!open);
  }

  function handleNotify() {
    const name = selectedGroup.title;

    notify.info({
      i18n: {
        i18nKey: 'feed:menuLeaveGroupNotify',
        values: { name },
      },
    });
  }

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
    setOpen(false);
  }

  function handleCompleted() {
    handleClose();
    handleNotify();
  }

  function handleSort() {
    handleClose();
    onSort();
  }

  function renderLeaveGroup(isFollowRequired: UserFeedFollow['required']) {
    if (isFollowRequired) {
      return null;
    }

    return <MenuItem onClick={toggleConfirm}>{t('feed:menuLeaveGroup')}</MenuItem>;
  }

  function renderGroupLinks() {
    const canEdit =
      isAdminUser(currentUser) || isGroupOwnerOrModerator(currentUser, selectedGroup.id);
    const canLeave = !isGroupOwnerOrModerator(currentUser, selectedGroup.id);
    const refetchQueryKey = useGetGroupFollowersQuery.getKey({
      data: { id: selectedGroup.id, limit: 10, feed: FeedKey.Group },
    });

    return (
      <div>
        {canEdit && (
          <MenuItem component={generateLink(`/group/${selectedGroup.id}/edit`)}>
            {t('groups:editGroup')}
          </MenuItem>
        )}
        {canLeave && (
          <Fragment>
            <FollowToggle
              source={selectedGroup as Group}
              type={FeedKey.Group}
              refetchQueryKeys={[refetchQueryKey]}
            >
              {({ onClick, isFollowRequired }) => (
                <Fragment>
                  <Alert
                    open={open}
                    onConfirm={() => {
                      void onClick();
                      handleCompleted();
                    }}
                    onCancel={toggleConfirm}
                    message={t('feed:menuLeaveGroupAlert')}
                    confirm={t('common:leave')}
                  />
                  {renderLeaveGroup(isFollowRequired)}
                </Fragment>
              )}
            </FollowToggle>
          </Fragment>
        )}
      </div>
    );
  }

  function renderSort() {
    if (!allowSort) {
      return null;
    }

    return (
      <div>
        <MenuItem selected={sort} disabled={sort} onClick={handleSort}>
          {t('feed:menuTrending')}
        </MenuItem>
        <MenuItem selected={!sort} disabled={!sort} onClick={handleSort}>
          {t('feed:menuChronologically')}
        </MenuItem>
      </div>
    );
  }

  return (
    <Fragment>
      <IconButton onClick={handleClick}>
        <MoreHorzIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
        {enableSort && renderSort()}
        {renderGroupLinks()}
      </Menu>
    </Fragment>
  );
};

FeedMenu.defaultProps = {
  enableSort: false,
  sort: false,
  onSort: () => undefined,
};

export default FeedMenu;
